import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, Input, OnChanges, OnInit, Signal, SimpleChanges } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Insurance } from '@lx/shared/data-access/products';
import { Amount } from '@lx/shared/data-access/types';
import { LocalizeAndPluralizePeriodPipe } from '@lx/shared/ui/pipes';
import { LoadingService } from '@lx/shared/ui/loading-spinner';
import { BehaviorSubject, Observable, take } from 'rxjs';
import { GoogleAnalyticsEventEnum } from '../../google-analytics';
import { QTLiteStoreSignalsAdapterService } from '../../services/qt-lite-store-signals-adapter.service';
import { QuoteOutputProduct, QuoteStore, QuoteToolLiteStore } from '../../store';
import { OperationKey } from '../../constants';
import { CardContainerComponent } from '@lx/quote-tool/lite/shared/ui/components';

@Component({
  standalone: true,
  selector: 'lx-insurance',
  templateUrl: './insurance.component.html',
  styleUrls: ['./insurance.component.scss'],
  imports: [CommonModule, CardContainerComponent, LocalizeAndPluralizePeriodPipe],
})
export class InsuranceComponent implements OnChanges, OnInit {
  @Input() insurance: Insurance | undefined;

  selectedInsuranceCode: string | undefined;
  price: Amount[] | undefined;
  endPrice: Amount[] | undefined;
  discount: string | undefined;
  duration: number | undefined;
  currency: Signal<string | undefined>;

  dataLayerEventName$: Observable<GoogleAnalyticsEventEnum> | undefined;

  protected loading: Signal<boolean | undefined>;
  private readonly dataLayerEventName = new BehaviorSubject<GoogleAnalyticsEventEnum>(undefined);

  private readonly quoteToolLiteStore = inject(QuoteToolLiteStore);
  private readonly qtLiteStoreSignalsAdapterService = inject(QTLiteStoreSignalsAdapterService);
  private readonly quoteStore = inject(QuoteStore);
  private readonly loadingService = inject(LoadingService);
  private readonly destroyRef = inject(DestroyRef);

  ngOnChanges({ insurance }: SimpleChanges) {
    this.quoteToolLiteStore.selectedDuration$.pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe((duration) => (this.duration = duration));
    if (insurance?.currentValue) {
      const totalPriceFromExtrasInsurance = this.calculateTotalFromExtrasInsurance(insurance.currentValue?.multiCurrencyPrice, this.duration);
      this.endPrice = totalPriceFromExtrasInsurance;
      this.price = totalPriceFromExtrasInsurance;
    }
  }

  ngOnInit(): void {
    this.quoteStore.insurance$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((quotedInsurance: QuoteOutputProduct) => {
      this.setPropertiesFromQuotedInsurance(quotedInsurance);
    });
    this.dataLayerEventName$ = this.dataLayerEventName.asObservable();
    this.currency = this.qtLiteStoreSignalsAdapterService.selectedCurrency;
    this.loading = this.loadingService.areAllOperationsLoading([OperationKey.prices, OperationKey.extras]);
  }

  addRemoveInsurance(selectedInsuranceCode: string) {
    if (!this.selectedInsuranceCode) {
      this.quoteToolLiteStore.updateState({ selectedInsuranceCode });
      this.dataLayerEventName.next(GoogleAnalyticsEventEnum.QuoteInsuranceAdd);
    } else {
      this.quoteToolLiteStore.updateState({ selectedInsuranceCode: undefined });
      this.dataLayerEventName.next(GoogleAnalyticsEventEnum.QuoteInsuranceRemove);
    }
  }

  /**
   * Calculates the total insurance price value multipliying the duration by the price value (price from extras)
   */
  private calculateTotalFromExtrasInsurance(multiCurrencyPrice: Amount[], duration: number): Amount[] {
    return multiCurrencyPrice?.map((price) => ({
      ...price,
      value: price.value * duration,
    }));
  }

  private setPropertiesFromQuotedInsurance(quotedInsurance: QuoteOutputProduct): void {
    this.selectedInsuranceCode = quotedInsurance?.code;
    this.endPrice = quotedInsurance?.endPrice || this.calculateTotalFromExtrasInsurance(this.insurance?.multiCurrencyPrice, this.duration);
    this.price = quotedInsurance?.price || this.endPrice;
    this.discount = quotedInsurance?.discountTotalAsPercentage;
  }
}
